import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import styles from '../styles/Projects.module.css';

const ProjectModal = ({ project, isOpen, onClose }) => {
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const autoPlayRef = useRef(null);
  
  const mediaItems = project?.media && project.media.length > 0 
    ? project.media 
    : (project?.image ? [project.image] : []);

  // Auto-loop carousel
  useEffect(() => {
    if (!isOpen || mediaItems.length <= 1 || isPaused || isHovered) return;

    autoPlayRef.current = setInterval(() => {
      setCurrentMediaIndex((prev) => (prev + 1) % mediaItems.length);
    }, 3000); // 3 seconds per image

    return () => clearInterval(autoPlayRef.current);
  }, [isOpen, mediaItems.length, isPaused, isHovered]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setCurrentMediaIndex(0);
      setIsPaused(false);
      setIsHovered(false);
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!project) return null;

  const nextMedia = () => {
    setIsPaused(true); // Pause auto-play when manually navigating
    setCurrentMediaIndex((prev) => (prev + 1) % mediaItems.length);
  };

  const prevMedia = () => {
    setIsPaused(true); // Pause auto-play when manually navigating
    setCurrentMediaIndex((prev) => (prev - 1 + mediaItems.length) % mediaItems.length);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles.modal__backdrop}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className={styles.modal__content}
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 30 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className={styles.modal__close} onClick={onClose} aria-label="Close modal">
              <FaTimes />
            </button>

            {/* Scrollable wrapper */}
            <div className={styles.modal__scroll}>
              {/* Image with 16:9 ratio */}
              <div 
                className={styles.modal__image_wrapper}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <AnimatePresence mode="wait">
                  <motion.img 
                    key={currentMediaIndex}
                    src={mediaItems[currentMediaIndex]} 
                    alt={`${project.title} screenshot`} 
                    className={styles.modal__image}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  />
                </AnimatePresence>
                
                {mediaItems.length > 1 && (
                  <>
                    <button 
                      className={`${styles.modal__nav_btn} ${styles.modal__nav_prev}`} 
                      onClick={prevMedia}
                    >
                      <FaChevronLeft />
                    </button>
                    <button 
                      className={`${styles.modal__nav_btn} ${styles.modal__nav_next}`} 
                      onClick={nextMedia}
                    >
                      <FaChevronRight />
                    </button>
                  </>
                )}
              </div>

              {/* Modal Body */}
              <div className={styles.modal__body}>
                <h2 className={styles.modal__title}>{project.title}</h2>
                
                <p className={styles.modal__description}>{project.description}</p>

                {/* Features */}
                {project.features && project.features.length > 0 && (
                  <div>
                    <h4 className={styles.modal__section_title}>Key Features</h4>
                    <ul className={styles.modal__features}>
                      {project.features.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Tech Stack */}
                <div>
                  <h4 className={styles.modal__section_title}>Technologies</h4>
                  <div className={styles.modal__tech_list}>
                    {project.tech.map((tech, idx) => (
                      <span key={idx} className={styles.modal__tech_tag}>{tech}</span>
                    ))}
                  </div>
                </div>

                {/* Links */}
                <div className={styles.modal__links}>
                  {project.links?.demo && (
                    <a
                      href={project.links.demo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${styles.modal__link} ${styles['modal__link--primary']}`}
                    >
                      <FaExternalLinkAlt /> Live Demo
                    </a>
                  )}
                  {project.links?.code && (
                    <a
                      href={project.links.code}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${styles.modal__link} ${styles['modal__link--secondary']}`}
                    >
                      <FaGithub /> View Code
                    </a>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProjectModal;
